import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Table, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const COLORS = {
  danger: '#FF4D4F',
  warning: '#FAAD14',
  blue: '#1890ff',
};

/**
 * Background color for duration badge by rank
 * 1st = gold, 2nd = silver, 3rd = bronze, rest = neutral gray
 */
const getDurationBadgeStyle = (index) => {
  const base = {
    border: 'none',
    WebkitFontSmoothing: 'antialiased',
  };
  if (index === 0) return {
    ...base,
    background: 'linear-gradient(180deg, #FECACA 0%, #FCA5A5 50%, #FEE2E2 100%)',
    color: '#991B1B',
    boxShadow: '0 4px 6px -1px rgba(239,68,68,0.25), 0 2px 4px -2px rgba(239,68,68,0.15), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)',
  };
  if (index === 1) return {
    ...base,
    background: 'linear-gradient(180deg, #FED7AA 0%, #FDBA74 50%, #FFEDD5 100%)',
    color: '#9A3412',
    boxShadow: '0 4px 6px -1px rgba(249,115,22,0.25), 0 2px 4px -2px rgba(249,115,22,0.15), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)',
  };
  if (index === 2) return {
    ...base,
    background: 'linear-gradient(180deg, #FEF08A 0%, #FDE047 50%, #FEF9C3 100%)',
    color: '#854D0E',
    boxShadow: '0 4px 6px -1px rgba(234,179,8,0.25), 0 2px 4px -2px rgba(234,179,8,0.15), inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -1px 0 rgba(0,0,0,0.05)',
  };
  return {
    ...base,
    background: 'linear-gradient(180deg, #F9FAFB 0%, #E5E7EB 50%, #F3F4F6 100%)',
    color: '#4B5563',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -2px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.8), inset 0 -1px 0 rgba(0,0,0,0.04)',
  };
};

/**
 * Rank medal for top 3
 */
const getRankDisplay = (index) => {
  if (index === 0) return <span style={{ fontSize: 18 }}>🥇</span>;
  if (index === 1) return <span style={{ fontSize: 18 }}>🥈</span>;
  if (index === 2) return <span style={{ fontSize: 18 }}>🥉</span>;
  return <span style={{ fontSize: 14, color: '#999', fontWeight: 'bold' }}>{index + 1}</span>;
};

/**
 * Module 3: Rankings & Actionables
 * Store Duration Table (with medals + duration badge) + Shortage Items Bar Chart
 */
const RankingsPanel = ({ data }) => {
  const { t } = useTranslation();
  const [storeCodeFilter, setStoreCodeFilter] = useState('');

  const storeDurationRanking = data?.storeDurationRanking || [];
  const shortageItemRanking = data?.shortageItemRanking || [];

  // Filter store data by code - must be called before any early return
  const filteredStoreData = useMemo(() => {
    if (!storeCodeFilter.trim()) return storeDurationRanking;
    const keyword = storeCodeFilter.trim().toLowerCase();
    return storeDurationRanking.filter(
      (item) => item.storeCode.toLowerCase().includes(keyword)
    );
  }, [storeDurationRanking, storeCodeFilter]);

  if (!data) return null;

  // Store Duration Table columns - equal width, no extra whitespace
  const storeColumns = [
    {
      title: '#',
      key: 'rank',
      width: '6%',
      align: 'center',
      render: (_, record) => {
        const originalIndex = storeDurationRanking.indexOf(record);
        return getRankDisplay(originalIndex);
      },
    },
    {
      title: t('dashboard.nationwide.storeName'),
      dataIndex: 'storeName',
      key: 'storeName',
      width: '30%',
      ellipsis: true,
    },
    {
      title: t('dashboard.nationwide.storeCode'),
      dataIndex: 'storeCode',
      key: 'storeCode',
      width: '16%',
    },
    {
      title: t('dashboard.nationwide.type'),
      dataIndex: 'storeType',
      key: 'storeType',
      width: '16%',
      align: 'center',
      render: (type) => {
        const typeConfig = {
          'Go Fresh': { color: '#52C41A', bg: '#F6FFED' },
          'Super': { color: '#1890FF', bg: '#E6F7FF' },
          'Hyper': { color: '#722ED1', bg: '#F9F0FF' },
        };
        const config = typeConfig[type] || { color: '#666', bg: '#F5F5F5' };
        return (
          <span style={{
            display: 'inline-block',
            padding: '2px 10px',
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
            color: config.color,
            background: config.bg,
            border: `1px solid ${config.color}30`,
          }}>
            {type}
          </span>
        );
      },
    },
    {
      title: t('dashboard.nationwide.totalDurationPerOrder'),
      key: 'avgDuration',
      align: 'center',
      render: (_, record) => {
        const originalIndex = storeDurationRanking.indexOf(record);
        const badgeStyle = getDurationBadgeStyle(originalIndex);
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '6px 18px',
              borderRadius: 20,
              fontSize: 13,
              fontWeight: 600,
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", "Helvetica Neue", sans-serif',
              letterSpacing: '0.2px',
              WebkitFontSmoothing: 'antialiased',
              backdropFilter: 'blur(8px)',
              transition: 'all 0.2s ease',
              ...badgeStyle,
            }}
          >
            {record.avgDuration} min
          </span>
        );
      },
    },
  ];

  // Gradient palette matching Total Pickers Status style (teal → coral)
  const ITEM_BAR_COLORS = [
    '#13C2C2', '#36CFC9', '#5CDBD3', '#87E8DE', '#B5F5EC',
    '#FFD6E7', '#FFADD2', '#FF85C0', '#FF7875', '#FFA39E',
  ];

  // 11. Shortage Items Ranking - Horizontal Bar Chart with colorful bars and medals
  // y 轴展示货品编码（item code / sku）
  const itemNames = shortageItemRanking.map((item) => item.sku || item.itemName || '-');
  const shortageCounts = shortageItemRanking.map((item) => item.shortageCount);

  const shortageItemOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const reversedIndex = shortageItemRanking.length - 1 - params[0].dataIndex;
        const item = shortageItemRanking[reversedIndex];
        const codes = item.affectedStoreCodes || [];
        return `<b>${item.itemName}</b><br/>
          SKU: ${item.sku}<br/>
          Shortage Count: <b>${item.shortageCount}</b><br/>
          Affected Stores (${codes.length}): ${codes.length ? codes.join(', ') : '-'}`;
      },
    },
    grid: { left: 160, right: 100, top: 10, bottom: 10 },
    xAxis: {
      type: 'value',
      axisLabel: { fontSize: 11 },
      splitLine: { lineStyle: { type: 'dashed', color: '#f0f0f0' } },
    },
    yAxis: {
      type: 'category',
      data: itemNames.slice().reverse(),
      axisLabel: {
        fontSize: 12,
        color: '#333',
        width: 140,
        overflow: 'truncate',
        align: 'left',
        margin: 150,
      },
      axisTick: { show: false },
      axisLine: { show: false },
    },
    series: [{
      type: 'bar',
      data: shortageCounts.slice().reverse().map((value, index) => {
        const originalRank = shortageItemRanking.length - 1 - index;
        return {
          value,
          itemStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
              { offset: 0, color: ITEM_BAR_COLORS[originalRank] || ITEM_BAR_COLORS[originalRank % ITEM_BAR_COLORS.length] },
              { offset: 1, color: (ITEM_BAR_COLORS[originalRank] || ITEM_BAR_COLORS[originalRank % ITEM_BAR_COLORS.length]) + 'CC' },
            ]),
            borderRadius: [0, 6, 6, 0],
          },
        };
      }),
      barWidth: 20,
      label: {
        show: true,
        position: 'right',
        formatter: (params) => {
          const originalRank = shortageItemRanking.length - 1 - params.dataIndex;
          const item = shortageItemRanking[originalRank];
          const codes = item.affectedStoreCodes || [];
          // 过长时只展示前 2 个 + 汇总数，完整列表靠 tooltip 浮显
          let codeStr;
          if (codes.length === 0) {
            codeStr = '-';
          } else if (codes.length <= 2) {
            codeStr = codes.join(', ');
          } else {
            codeStr = `${codes.slice(0, 2).join(', ')} +${codes.length - 2}`;
          }
          return `${params.value} (${codeStr})`;
        },
        fontSize: 11,
        color: '#666',
      },
    }],
  };

  return (
    <div style={{ display: 'flex', gap: 16, minWidth: 0 }}>
      {/* 10. Store Duration Table with medals and duration badges */}
      <Card
        title={t('dashboard.nationwide.storeDurationTitle')}
        extra={
          <Input
            placeholder={t('dashboard.nationwide.searchStoreCode')}
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={storeCodeFilter}
            onChange={(e) => setStoreCodeFilter(e.target.value)}
            allowClear
            size="small"
            style={{ width: 180 }}
          />
        }
        style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        styles={{ body: { padding: '0 0 8px 0' } }}
      >
        <Table
          columns={storeColumns}
          dataSource={filteredStoreData}
          rowKey="storeCode"
          pagination={false}
          size="small"
          scroll={{ y: 340 }}
          tableLayout="fixed"
        />
      </Card>

      {/* 11. Shortage Items Ranking */}
      <Card
        title={t('dashboard.nationwide.shortageItemsRanking')}
        style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}
        styles={{ body: { padding: '8px 16px' } }}
      >
        <ReactEChartsCore
          echarts={echarts}
          option={shortageItemOption}
          style={{ height: 340 }}
          opts={{ renderer: 'canvas' }}
        />
      </Card>
    </div>
  );
};

export default RankingsPanel;
